<template>
  <ImageValidation :platform-validation="platformValidation" :is-validating="isValidating" />
</template>

<script lang="ts" setup>
import { inject, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import ImageValidation from './ImageValidation.vue'
import { useAssetStore } from '@/stores/asset'
import { useAppStore } from '@/stores/app'
import { useCanvasStore } from '@/stores/canvas'
import { usePlacementStore } from '@/stores/placement'
import type { Validation } from '@/types/validation'
import type { FooterConfigProvider } from '@/types/footerConfig'
import { validateWithCanvasJson } from '@/helpers/creativeHelper'
import { logCreative } from '@/helpers/commonHelper'
import { embedMessageTypes } from '@/constants/embed'

const route = useRoute()
const assetStore = useAssetStore()
const appStore = useAppStore()
const placementStore = usePlacementStore()
const canvasStore = useCanvasStore()
const { t } = useI18n()
const notify = inject('notify') as Function
const { setFooterConfig } = inject<FooterConfigProvider>('footerConfig')!

const isValidating = ref<boolean>(false)
const platformValidation = ref<Validation[]>([])
const validationError = ref<boolean>(false)

const validateCreative = async () => {
  try {
    if (assetStore.creativeS3Url) {
      isValidating.value = true
      validationError.value = false

      if (!placementStore.selectedPlacement?.creativeSetGuidelines) {
        throw new Error('Guidelines not found')
      }
      const results = validateWithCanvasJson(
        placementStore.selectedPublishedVariant?.canvasJson,
        placementStore.selectedPlacement?.creativeSetGuidelines,
        canvasStore.canvasJson,
      )
      platformValidation.value = results.validations
      window.top?.postMessage({ type: embedMessageTypes.CREATIVE_VALIDATIONS, value: results }, '*')
      appStore.incrementStepperCount()
      logCreative(true)
    }
  } catch {
    notify(t('errors.somethingWentWrong'), 'error')
    validationError.value = true
  } finally {
    isValidating.value = false
  }
}

onMounted(() => {
  setFooterConfig({
    showBack: true,
    showFeedback: true,
    showDownload: true,
    showRestart: true,
  })
  window.onbeforeunload = () => {
    if (route.name === 'Download') return ''
  }
  if (appStore.stepperCount != 2) {
    appStore.setStepperCount(2)
  }
  validateCreative()
})

onUnmounted(() => {
  setFooterConfig({
    showBack: false,
    showFeedback: false,
    showDownload: false,
    showRestart: false,
  })
  window.onbeforeunload = null
})
</script>
